import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { XCloseIcon, MarkerPin01Icon, Loading01Icon, SearchMdIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { usePlaceAutocomplete, usePlaceDetails, type PlacePrediction } from '@/hooks/api'
import { Input } from '@/components/ui/Input'

interface LocationStepProps {
  placeId: string
  templateId: number
  onPlaceIdChange: (placeId: string) => void
  onTemplateIdChange: (templateId: number) => void
  showTemplateId?: boolean
}

/**
 * Step 1: Location selection
 * Allows users to search for their business using Google Places.
 */
export function LocationStep({
  placeId,
  templateId,
  onPlaceIdChange,
  onTemplateIdChange,
  showTemplateId = true,
}: LocationStepProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Fetch autocomplete predictions
  const { data: predictions = [], isLoading: isLoadingPredictions } = usePlaceAutocomplete(
    inputValue,
    isOpen && inputValue.length >= 3
  )

  // Fetch place details for selected place
  const { data: placeDetails, isLoading: isLoadingDetails } = usePlaceDetails(placeId)

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle selecting a prediction
  const handleSelect = useCallback(
    (prediction: PlacePrediction) => {
      onPlaceIdChange(prediction.place_id)
      setInputValue('')
      setIsOpen(false)
      setSelectedIndex(-1)
    },
    [onPlaceIdChange]
  )

  // Handle clearing the selection
  const handleClear = useCallback(() => {
    onPlaceIdChange('')
    setInputValue('')
  }, [onPlaceIdChange])

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!predictions.length) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : prev))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && predictions[selectedIndex]) {
            handleSelect(predictions[selectedIndex])
          }
          break
        case 'Escape':
          setIsOpen(false)
          setSelectedIndex(-1)
          break
      }
    },
    [predictions, selectedIndex, handleSelect]
  )

  // Reset selected index when predictions change
  useEffect(() => {
    setSelectedIndex(-1)
  }, [predictions])

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.onboarding.location.title')}
        </h2>
        <p className="mt-1 text-base text-gray-600 dark:text-gray-400">
          {t('resources.onboarding.location.description')}
        </p>
      </div>

      {/* Selected place card */}
      {placeId && placeDetails && (
        <div className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          {/* Place photo */}
          {placeDetails.photoUrl ? (
            <img
              src={placeDetails.photoUrl}
              alt={placeDetails.name}
              className="h-14 w-14 flex-shrink-0 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-brand-100 dark:bg-brand-900">
              <MarkerPin01Icon className="h-7 w-7 text-brand-600 dark:text-brand-400" />
            </div>
          )}

          {/* Place info */}
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-base font-medium text-gray-900 dark:text-gray-100">
              {placeDetails.name}
            </span>
            {placeDetails.address && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {placeDetails.address}
              </span>
            )}
            {placeDetails.city && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {placeDetails.city}
              </span>
            )}
          </div>

          {/* Remove button */}
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            {t('resources.onboarding.location.remove')}
            <XCloseIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Loading state while fetching details */}
      {placeId && isLoadingDetails && !placeDetails && (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
          <Loading01Icon className="h-5 w-5 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500">{t('resources.onboarding.location.loading_place_details')}</span>
        </div>
      )}

      {/* Search input (hidden when place is selected) */}
      {!placeId && (
        <div ref={containerRef} className="relative">
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value)
                setIsOpen(true)
              }}
              onFocus={() => setIsOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder={t('resources.onboarding.location.search_placeholder')}
              className={cn(
                'w-full rounded-xl border border-gray-300 bg-white px-4 py-3 pl-11 text-base',
                'placeholder:text-gray-400',
                'focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 focus:outline-none',
                'dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100',
                'dark:focus:border-brand-400 dark:focus:ring-brand-400/20'
              )}
            />
            <SearchMdIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            {isLoadingPredictions && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <Loading01Icon className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            )}
          </div>

          {/* Predictions dropdown */}
          {isOpen && predictions.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-64 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
              {predictions.map((prediction, index) => (
                <button
                  key={prediction.place_id}
                  type="button"
                  onClick={() => handleSelect(prediction)}
                  className={cn(
                    'w-full px-4 py-3 text-left',
                    'hover:bg-gray-50 dark:hover:bg-gray-800',
                    selectedIndex === index && 'bg-gray-50 dark:bg-gray-800'
                  )}
                >
                  {prediction.structured_formatting ? (
                    <>
                      <span className="block font-medium text-gray-900 dark:text-gray-100">
                        {prediction.structured_formatting.main_text}
                      </span>
                      <span className="mt-0.5 block text-sm text-gray-500 dark:text-gray-400">
                        {prediction.structured_formatting.secondary_text}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-900 dark:text-gray-100">
                      {prediction.description}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Template ID input (admin-only, hidden by default) */}
      {showTemplateId && (
        <Input
          label={t('resources.onboarding.location.template_id')}
          description={t('resources.onboarding.location.template_id_description')}
          type="number"
          value={templateId === 0 ? '' : templateId.toString()}
          onChange={(value) => {
            if (value === '') {
              onTemplateIdChange(0)
            } else {
              const parsed = parseInt(value, 10)
              if (!isNaN(parsed)) {
                onTemplateIdChange(parsed)
              }
            }
          }}
          className="max-w-xs"
        />
      )}
    </div>
  )
}

export default LocationStep
